"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return Model;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _dayjs = /*#__PURE__*/ _interop_require_default(require("dayjs"));
const _common = require("@nestjs/common");
const _objecthash = /*#__PURE__*/ _interop_require_default(require("object-hash"));
const _config = require("~/interface/config");
const _Hook = /*#__PURE__*/ _interop_require_default(require("~/models/Hook"));
const _View = /*#__PURE__*/ _interop_require_default(require("~/models/View"));
const _Comment = /*#__PURE__*/ _interop_require_default(require("~/models/Comment"));
const _Column = /*#__PURE__*/ _interop_require_default(require("~/models/Column"));
const _extractProps = require("~/helpers/extractProps");
const _sqlSanitize = require("~/helpers/sqlSanitize");
const _catchError = require("~/helpers/catchError");
const _globals = require("~/utils/globals");
const _NocoCache = /*#__PURE__*/ _interop_require_default(require("~/cache/NocoCache"));
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _BaseModelSqlv2 = require("~/db/BaseModelSqlv2");
const _models = require("~/models");
const _commandPaletteHelpers = require("~/helpers/commandPaletteHelpers");
const _modelUtils = require("~/utils/modelUtils");
const _scriptHelper = require("~/helpers/scriptHelper");
const _dbHelpers = require("~/helpers/dbHelpers");
const _utils = require("~/utils");
const _nccachedecorator = require("~/decorators/nc-cache.decorator");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
const logger = new _common.Logger('Model');
const modelOrViewXcCondition = {
    _or: [
        {
            type: {
                eq: _nocodbsdk.ModelTypes.TABLE
            }
        },
        {
            type: {
                eq: _nocodbsdk.ModelTypes.VIEW
            }
        }
    ]
};
let Model = class Model {
    static castType(data) {
        return data && new Model(data);
    }
    async getColumns(context, ncMeta = _Noco.default.ncMeta, defaultViewId = undefined, updateColumns = true) {
        const columns = await _Column.default.list(context, {
            fk_model_id: this.id,
            fk_default_view_id: defaultViewId
        }, ncMeta);
        if (!updateColumns) return columns;
        this.columns = columns;
        this.columnsById = this.columns.reduce((agg, c)=>{
            agg[c.id] = c;
            return agg;
        }, {});
        return this.columns;
    }
    async getColumnsHash(context, ncMeta = _Noco.default.ncMeta) {
        const columns = await this.getColumns(context, ncMeta, undefined, false);
        return this.columnsHash = (0, _objecthash.default)(columns);
    }
    // get columns cached under the instance or fetch from db/redis cache
    async getCachedColumns(context, ncMeta = _Noco.default.ncMeta) {
        if (this.columns) return this.columns;
        return this.getColumns(context, ncMeta);
    }
    // @ts-ignore
    async getViews(context, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    force = false, ncMeta = _Noco.default.ncMeta) {
        this.views = await _View.default.listWithInfo(context, this.id, ncMeta);
        return this.views;
    }
    get primaryKey() {
        var _this_columns;
        if (!this.columns) return null;
        //  return first auto increment or auto generated column
        // if not found return first pk column
        return this.columns.find((c)=>{
            var _c_meta;
            return c.pk && (c.ai || ((_c_meta = c.meta) == null ? void 0 : _c_meta.ag));
        }) || ((_this_columns = this.columns) == null ? void 0 : _this_columns.find((c)=>c.pk));
    }
    get primaryKeys() {
        var _this_columns;
        if (!this.columns) return null;
        return (_this_columns = this.columns) == null ? void 0 : _this_columns.filter((c)=>c.pk);
    }
    // If there is no column marked as display value,
    // we are getting the immediate next column to pk as display value
    // or the first column(if pk is the last column).
    get displayValue() {
        var _this_columns;
        if (!this.columns) return null;
        const pCol = (_this_columns = this.columns) == null ? void 0 : _this_columns.find((c)=>c.pv);
        if (pCol) return pCol;
        if (this.mm) {
            // by default, there is no default value in m2m table
            // take the first column instead
            return this.columns[0];
        }
        const pkIndex = this.columns.indexOf(this.primaryKey);
        if (pkIndex < this.columns.length - 1) return this.columns[pkIndex + 1];
        return this.columns[0];
    }
    static async insert(context, baseId, sourceId, model, ncMeta = _Noco.default.ncMeta) {
        const insertObj = (0, _extractProps.extractProps)(model, [
            'table_name',
            'title',
            'description',
            'mm',
            'order',
            'type',
            'id',
            'meta',
            'synced'
        ]);
        insertObj.mm = !!insertObj.mm;
        const condition = {
            base_id: baseId
        };
        if (_utils.isEE) {
            condition.fk_workspace_id = context.workspace_id;
        }
        if (!insertObj.order) {
            insertObj.order = await ncMeta.metaGetNextOrder(_globals.MetaTable.MODELS, condition, {
                _or: [
                    {
                        source_id: {
                            eq: sourceId
                        }
                    },
                    {
                        source_id: {
                            eq: null
                        }
                    }
                ]
            });
        }
        if (!insertObj.type) {
            insertObj.type = _nocodbsdk.ModelTypes.TABLE;
        }
        insertObj.source_id = sourceId;
        const { id } = await ncMeta.metaInsert2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, insertObj);
        const insertedColumns = await _Column.default.bulkInsert(context, {
            columns: (model == null ? void 0 : model.columns) || [],
            fk_model_id: id,
            source_id: sourceId,
            base_id: baseId
        }, ncMeta);
        await _View.default.insertMetaOnly(context, {
            view: {
                fk_model_id: id,
                title: model.title || model.table_name,
                type: _nocodbsdk.ViewTypes.GRID,
                base_id: baseId,
                source_id: sourceId,
                created_by: model.user_id,
                owned_by: model.user_id
            },
            model: {
                getColumns: async ()=>insertedColumns
            },
            req: {
                user: {}
            }
        }, ncMeta);
        const modelRes = await this.getWithInfo(context, {
            id
        }, ncMeta);
        // append to model list since model list cache will be there already
        if (sourceId) {
            await _NocoCache.default.appendToList(context, _globals.CacheScope.MODEL, [
                baseId,
                sourceId
            ], `${_globals.CacheScope.MODEL}:${id}`);
        }
        // cater cases where sourceId is not required
        // e.g. xcVisibilityMetaGet
        await _NocoCache.default.appendToList(context, _globals.CacheScope.MODEL, [
            baseId
        ], `${_globals.CacheScope.MODEL}:${id}`);
        (0, _commandPaletteHelpers.cleanCommandPaletteCache)(context.workspace_id).catch(()=>{
            logger.error('Failed to clean command palette cache');
        });
        (0, _scriptHelper.cleanBaseSchemaCacheForBase)(context.base_id).catch(()=>{
            logger.error('Failed to clean base schema cache');
        });
        return modelRes;
    }
    static async list(context, { base_id, source_id }, ncMeta = _Noco.default.ncMeta) {
        const cachedList = await _NocoCache.default.getList(context, _globals.CacheScope.MODEL, [
            base_id,
            ...source_id ? [
                source_id
            ] : []
        ]);
        let { list: modelList } = cachedList;
        const { isNoneList } = cachedList;
        if (!isNoneList && !modelList.length) {
            modelList = await ncMeta.metaList2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, _extends({
                orderBy: {
                    order: 'asc'
                }
            }, source_id ? {
                condition: {
                    source_id
                }
            } : {}, {
                xcCondition: modelOrViewXcCondition
            }));
            // parse meta of each model
            for (const model of modelList){
                model.meta = (0, _modelUtils.parseMetaProp)(model);
            }
            // set cache based on source_id presence
            if (source_id) {
                await _NocoCache.default.setList(context, _globals.CacheScope.MODEL, [
                    base_id,
                    source_id
                ], modelList);
            } else {
                await _NocoCache.default.setList(context, _globals.CacheScope.MODEL, [
                    base_id
                ], modelList);
            }
        }
        modelList.sort((a, b)=>(a.order != null ? a.order : Infinity) - (b.order != null ? b.order : Infinity));
        return modelList.map((m)=>this.castType(m));
    }
    static async listWithInfo(context, { base_id, db_alias }, ncMeta = _Noco.default.ncMeta) {
        const cachedList = await _NocoCache.default.getList(context, _globals.CacheScope.MODEL, [
            base_id,
            db_alias
        ]);
        let { list: modelList } = cachedList;
        const { isNoneList } = cachedList;
        if (!isNoneList && !modelList.length) {
            modelList = await ncMeta.metaList2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
                xcCondition: modelOrViewXcCondition
            });
            // parse meta of each model
            for (const model of modelList){
                model.meta = (0, _modelUtils.parseMetaProp)(model);
            }
            await _NocoCache.default.setList(context, _globals.CacheScope.MODEL, [
                base_id
            ], modelList);
        }
        return modelList.map((m)=>this.castType(m));
    }
    static async get(context, id, ncMeta = _Noco.default.ncMeta) {
        let modelData = id && await _NocoCache.default.get(context, `${_globals.CacheScope.MODEL}:${id}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!modelData) {
            modelData = await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, id, undefined, modelOrViewXcCondition);
            if (modelData) {
                modelData.meta = (0, _modelUtils.parseMetaProp)(modelData);
                await _NocoCache.default.set(context, `${_globals.CacheScope.MODEL}:${modelData.id}`, modelData);
            }
        }
        return this.castType(modelData);
    }
    static async getByIdOrName(context, args, ncMeta = _Noco.default.ncMeta) {
        if ('id' in args && (args == null ? void 0 : args.id)) {
            return this.get(context, args.id, ncMeta);
        }
        const k = 'id' in args ? args == null ? void 0 : args.id : args;
        let modelData = k && await _NocoCache.default.get(context, `${_globals.CacheScope.MODEL}:${k}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!modelData) {
            modelData = await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, k, undefined, modelOrViewXcCondition);
            if (modelData) {
                modelData.meta = (0, _modelUtils.parseMetaProp)(modelData);
            }
        }
        if (modelData) {
            modelData.meta = (0, _modelUtils.parseMetaProp)(modelData);
            await _NocoCache.default.set(context, `${_globals.CacheScope.MODEL}:${modelData.id}`, modelData);
            return this.castType(modelData);
        }
        return null;
    }
    static async getWithInfo(context, { table_name, id }, ncMeta = _Noco.default.ncMeta) {
        let modelData = id && await _NocoCache.default.get(context, `${_globals.CacheScope.MODEL}:${id}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!modelData) {
            modelData = await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, id || {
                table_name
            }, undefined, {
                _or: [
                    {
                        type: {
                            eq: _nocodbsdk.ModelTypes.TABLE
                        }
                    },
                    {
                        type: {
                            eq: _nocodbsdk.ModelTypes.VIEW
                        }
                    }
                ]
            });
            if (modelData) {
                modelData.meta = (0, _modelUtils.parseMetaProp)(modelData);
                await _NocoCache.default.set(context, `${_globals.CacheScope.MODEL}:${modelData.id}`, modelData);
            }
        // modelData.filters = await Filter.getFilterObject({
        //   viewId: modelData.id
        // });
        // modelData.sorts = await Sort.list({ modelId: modelData.id });
        }
        if (modelData) {
            var _getFirstNonPersonalView;
            const m = this.castType(modelData);
            await m.getViews(context, false, ncMeta);
            const defaultViewId = (_getFirstNonPersonalView = (0, _nocodbsdk.getFirstNonPersonalView)(m.views, {
                includeViewType: _nocodbsdk.ViewTypes.GRID
            })) == null ? void 0 : _getFirstNonPersonalView.id;
            await m.getColumns(context, ncMeta, defaultViewId);
            await m.getColumnsHash(context, ncMeta);
            return m;
        }
        return null;
    }
    /**
   * Creates a BaseModelSqlv2 instance with optional transaction support.
   *
   * @param context - The NocoDB context
   * @param args - Configuration arguments
   * @param args.dbDriver - The base database driver
   * @param args.transaction - Optional transaction instance to use for operations
   * @param args.model - The model instance (optional, will be fetched if not provided)
   * @param args.extractFirstCollaborativeView - Whether to extract the first collaborative grid view if viewId not provided
   * @param args.viewId - The view ID (optional)
   * @param args.source - The data source (optional, will be fetched if not provided)
   * @param ncMeta - The NocoDB metadata instance
   * @returns A configured BaseModelSqlv2 instance
   */ static async getBaseModelSQL(context, args, ncMeta = _Noco.default.ncMeta) {
        const model = (args == null ? void 0 : args.model) || await this.get(context, args.id, ncMeta);
        const source = args.source || await _models.Source.get(context, model.source_id, false, ncMeta);
        if (!(args == null ? void 0 : args.viewId) && args.extractFirstCollaborativeView) {
            const view = await _View.default.getFirstCollaborativeView(context, model.id, ncMeta);
            args.viewId = view.id;
        }
        let schema;
        if (source == null ? void 0 : source.isMeta(true, 1)) {
            var _source_getConfig;
            schema = (_source_getConfig = source.getConfig()) == null ? void 0 : _source_getConfig.schema;
        } else if ((source == null ? void 0 : source.type) === 'pg') {
            var _source_getConfig_searchPath, _source_getConfig1;
            schema = (_source_getConfig1 = source.getConfig()) == null ? void 0 : (_source_getConfig_searchPath = _source_getConfig1.searchPath) == null ? void 0 : _source_getConfig_searchPath[0];
        }
        return new _BaseModelSqlv2.BaseModelSqlv2({
            context,
            dbDriver: args.dbDriver,
            transaction: args.transaction,
            viewId: args.viewId,
            model,
            schema
        });
    }
    async delete(context, ncMeta = _Noco.default.ncMeta, force = false) {
        await _Comment.default.deleteModelComments(context, this.id, ncMeta);
        for (const view of (await this.getViews(context, true, ncMeta))){
            await view.delete(context, ncMeta);
        }
        // delete associated hooks
        for (const hook of (await _Hook.default.list(context, {
            fk_model_id: this.id
        }, ncMeta))){
            await _Hook.default.delete(context, hook.id, ncMeta);
        }
        for (const col of (await this.getColumns(context, ncMeta))){
            let colOptionTableName = null;
            let cacheScopeName = null;
            switch(col.uidt){
                case _nocodbsdk.UITypes.Rollup:
                    colOptionTableName = _globals.MetaTable.COL_ROLLUP;
                    cacheScopeName = _globals.CacheScope.COL_ROLLUP;
                    break;
                case _nocodbsdk.UITypes.Lookup:
                    colOptionTableName = _globals.MetaTable.COL_LOOKUP;
                    cacheScopeName = _globals.CacheScope.COL_LOOKUP;
                    break;
                case _nocodbsdk.UITypes.ForeignKey:
                case _nocodbsdk.UITypes.LinkToAnotherRecord:
                    colOptionTableName = _globals.MetaTable.COL_RELATIONS;
                    cacheScopeName = _globals.CacheScope.COL_RELATION;
                    break;
                case _nocodbsdk.UITypes.MultiSelect:
                case _nocodbsdk.UITypes.SingleSelect:
                    colOptionTableName = _globals.MetaTable.COL_SELECT_OPTIONS;
                    cacheScopeName = _globals.CacheScope.COL_SELECT_OPTION;
                    break;
                case _nocodbsdk.UITypes.Formula:
                    colOptionTableName = _globals.MetaTable.COL_FORMULA;
                    cacheScopeName = _globals.CacheScope.COL_FORMULA;
                    break;
                case _nocodbsdk.UITypes.QrCode:
                    colOptionTableName = _globals.MetaTable.COL_QRCODE;
                    cacheScopeName = _globals.CacheScope.COL_QRCODE;
                    break;
                case _nocodbsdk.UITypes.Barcode:
                    colOptionTableName = _globals.MetaTable.COL_BARCODE;
                    cacheScopeName = _globals.CacheScope.COL_BARCODE;
                    break;
            }
            if (colOptionTableName && cacheScopeName) {
                await ncMeta.metaDelete(context.workspace_id, context.base_id, colOptionTableName, {
                    fk_column_id: col.id
                });
                await _NocoCache.default.deepDel(context, `${cacheScopeName}:${col.id}`, _globals.CacheDelDirection.CHILD_TO_PARENT);
            }
        }
        if (force) {
            const leftOverColumns = await ncMeta.metaList2(context.workspace_id, context.base_id, _globals.MetaTable.COL_RELATIONS, {
                condition: {
                    fk_related_model_id: this.id
                }
            });
            for (const col of leftOverColumns){
                await _NocoCache.default.deepDel(context, `${_globals.CacheScope.COL_RELATION}:${col.fk_column_id}`, _globals.CacheDelDirection.CHILD_TO_PARENT);
            }
            await ncMeta.metaDelete(context.workspace_id, context.base_id, _globals.MetaTable.COL_RELATIONS, {
                fk_related_model_id: this.id
            });
        }
        await _NocoCache.default.deepDel(context, `${_globals.CacheScope.COLUMN}:${this.id}`, _globals.CacheDelDirection.CHILD_TO_PARENT);
        await ncMeta.metaDelete(context.workspace_id, context.base_id, _globals.MetaTable.COLUMNS, {
            fk_model_id: this.id
        });
        // Delete FileReference
        await _models.FileReference.bulkDelete(context, {
            fk_model_id: this.id
        }, ncMeta);
        await _NocoCache.default.deepDel(context, `${_globals.CacheScope.MODEL}:${this.id}`, _globals.CacheDelDirection.CHILD_TO_PARENT);
        await ncMeta.metaDelete(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, this.id);
        // delete alias cache
        await _NocoCache.default.del(context, [
            `${_globals.CacheScope.MODEL_ALIAS}:${this.base_id}:${this.id}`,
            `${_globals.CacheScope.MODEL_ALIAS}:${this.base_id}:${this.source_id}:${this.id}`,
            `${_globals.CacheScope.MODEL_ALIAS}:${this.base_id}:${this.title}`,
            `${_globals.CacheScope.MODEL_ALIAS}:${this.base_id}:${this.source_id}:${this.title}`
        ]);
        (0, _commandPaletteHelpers.cleanCommandPaletteCache)(context.workspace_id).catch(()=>{
            logger.error('Failed to clean command palette cache');
        });
        (0, _scriptHelper.cleanBaseSchemaCacheForBase)(context.base_id).catch(()=>{
            logger.error('Failed to clean base schema cache');
        });
        return true;
    }
    async mapAliasToColumn(context, data, clientMeta = {
        isMySQL: false,
        isSqlite: false,
        isPg: false
    }, knex, columns) {
        const dbDataWrapper = (0, _dbHelpers.dataWrapper)(data);
        const insertObj = {};
        for (const col of columns || await this.getColumns(context)){
            if ((0, _nocodbsdk.isVirtualCol)(col)) continue;
            let val = dbDataWrapper.getByColumnNameTitleOrId(col);
            if (val !== undefined) {
                if (col.uidt === _nocodbsdk.UITypes.Attachment && typeof val !== 'string') {
                    val = JSON.stringify(val);
                }
                if (context.api_version !== _nocodbsdk.NcApiVersion.V3 && col.uidt === _nocodbsdk.UITypes.DateTime && (0, _dayjs.default)(val).isValid()) {
                    const { isMySQL, isSqlite, isPg } = clientMeta;
                    if (val.indexOf('-') < 0 && val.indexOf('+') < 0 && val.slice(-1) !== 'Z') {
                        // if no timezone is given,
                        // then append +00:00 to make it as UTC
                        val += '+00:00';
                    }
                    if (isMySQL) {
                        // first convert the value to utc
                        // from UI
                        // e.g. 2022-01-01 20:00:00Z -> 2022-01-01 20:00:00
                        // from API
                        // e.g. 2022-01-01 20:00:00+08:00 -> 2022-01-01 12:00:00
                        // if timezone info is not found - considered as utc
                        // e.g. 2022-01-01 20:00:00 -> 2022-01-01 20:00:00
                        // if timezone info is found
                        // e.g. 2022-01-01 20:00:00Z -> 2022-01-01 20:00:00
                        // e.g. 2022-01-01 20:00:00+00:00 -> 2022-01-01 20:00:00
                        // e.g. 2022-01-01 20:00:00+08:00 -> 2022-01-01 12:00:00
                        // then we use CONVERT_TZ to convert that in the db timezone
                        val = knex.raw(`CONVERT_TZ(?, '+00:00', @@GLOBAL.time_zone)`, [
                            (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ss')
                        ]);
                    } else if (isSqlite) {
                        // convert to UTC
                        // e.g. 2022-01-01T10:00:00.000Z -> 2022-01-01 04:30:00+00:00
                        val = (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ssZ');
                    } else if (isPg) {
                        // convert to UTC
                        // e.g. 2023-01-01T12:00:00.000Z -> 2023-01-01 12:00:00+00:00
                        // then convert to db timezone
                        val = knex.raw(`? AT TIME ZONE CURRENT_SETTING('timezone')`, [
                            (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ssZ')
                        ]);
                    } else {
                        // e.g. 2023-01-01T12:00:00.000Z -> 2023-01-01 12:00:00+00:00
                        val = (0, _dayjs.default)(val).utc().format('YYYY-MM-DD HH:mm:ssZ');
                    }
                }
                insertObj[(0, _sqlSanitize.sanitize)(col.column_name)] = val;
                if (clientMeta.isPg && col.dt === 'bytea') {
                    var _col_meta, _col_meta1;
                    insertObj[(0, _sqlSanitize.sanitize)(col.column_name)] = knex.raw(`decode(?, '${((_col_meta = col.meta) == null ? void 0 : _col_meta.format) === 'hex' ? 'hex' : 'escape'}')`, [
                        ((_col_meta1 = col.meta) == null ? void 0 : _col_meta1.format) === 'hex' && (val + '').length % 2 === 1 ? '0' + val : val
                    ]);
                }
            }
        }
        return insertObj;
    }
    async mapColumnToAlias(context, data, columns) {
        const res = {};
        const dbDataWrapper = (0, _dbHelpers.dataWrapper)(data);
        for (const col of columns || await this.getColumns(context)){
            if ((0, _nocodbsdk.isVirtualCol)(col)) continue;
            let val = dbDataWrapper.getByColumnNameTitleOrId(col);
            if (val !== undefined) {
                if (col.uidt === _nocodbsdk.UITypes.Attachment && typeof val !== 'string') {
                    val = JSON.stringify(val);
                }
                res[(0, _sqlSanitize.sanitize)(col.title)] = val;
            }
        }
        return res;
    }
    static async updateAliasAndTableName(context, tableId, title, table_name, ncMeta = _Noco.default.ncMeta) {
        if (!title) {
            _catchError.NcError.badRequest("Missing 'title' property in body");
        }
        if (!table_name) {
            _catchError.NcError.badRequest("Missing 'table_name' property in body");
        }
        const oldModel = await this.get(context, tableId, ncMeta);
        // set meta
        const res = await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
            title,
            table_name
        }, tableId);
        await _NocoCache.default.update(context, `${_globals.CacheScope.MODEL}:${tableId}`, {
            title,
            table_name
        });
        // delete alias cache
        await _NocoCache.default.del(context, [
            `${_globals.CacheScope.MODEL_ALIAS}:${oldModel.base_id}:${oldModel.id}`,
            `${_globals.CacheScope.MODEL_ALIAS}:${oldModel.base_id}:${oldModel.source_id}:${oldModel.id}`,
            `${_globals.CacheScope.MODEL_ALIAS}:${oldModel.base_id}:${oldModel.title}`,
            `${_globals.CacheScope.MODEL_ALIAS}:${oldModel.base_id}:${oldModel.source_id}:${oldModel.title}`
        ]);
        (0, _commandPaletteHelpers.cleanCommandPaletteCache)(context.workspace_id).catch(()=>{
            logger.error('Failed to clean command palette cache');
        });
        // clear all the cached query under this model
        await _View.default.clearSingleQueryCache(context, tableId, null, ncMeta);
        // clear all the cached query under related models
        for (const col of (await this.get(context, tableId).then((t)=>t.getColumns(context)))){
            if (!(0, _nocodbsdk.isLinksOrLTAR)(col)) continue;
            const colOptions = await col.getColOptions(context, ncMeta);
            if (colOptions.fk_related_model_id === tableId) continue;
            await _View.default.clearSingleQueryCache(context, colOptions.fk_related_model_id, null, ncMeta);
        }
        return res;
    }
    static async markAsMmTable(context, tableId, isMm = true, ncMeta = _Noco.default.ncMeta) {
        // set meta
        const res = await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
            mm: isMm
        }, tableId);
        await _NocoCache.default.update(context, `${_globals.CacheScope.MODEL}:${tableId}`, {
            mm: isMm
        });
        return res;
    }
    async getAliasColMapping(context) {
        return (await this.getColumns(context)).reduce((o, c)=>{
            if (c.column_name) {
                o[c.title] = c.column_name;
            }
            return o;
        }, {});
    }
    async getColAliasMapping(context) {
        return (await this.getColumns(context)).reduce((o, c)=>{
            if (c.column_name) {
                o[c.column_name] = c.title;
            }
            return o;
        }, {});
    }
    static async updateOrder(context, tableId, order, ncMeta = _Noco.default.ncMeta) {
        // set meta
        const res = await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
            order
        }, tableId);
        await _NocoCache.default.update(context, `${_globals.CacheScope.MODEL}:${tableId}`, {
            order
        });
        return res;
    }
    static async updatePrimaryColumn(context, tableId, columnId, ncMeta = _Noco.default.ncMeta) {
        var _model_columns;
        const model = await this.getWithInfo(context, {
            id: tableId
        }, ncMeta);
        const newPvCol = model.columnsById[columnId];
        if (!newPvCol) _catchError.NcError.fieldNotFound(columnId);
        // drop existing primary column/s
        for (const col of ((_model_columns = model.columns) == null ? void 0 : _model_columns.filter((c)=>c.pv)) || []){
            // set meta
            await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.COLUMNS, {
                pv: false
            }, col.id);
            await _NocoCache.default.update(context, `${_globals.CacheScope.COLUMN}:${col.id}`, {
                pv: false
            });
        }
        // set meta
        await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.COLUMNS, {
            pv: true
        }, newPvCol.id);
        await _NocoCache.default.update(context, `${_globals.CacheScope.COLUMN}:${newPvCol.id}`, {
            pv: true
        });
        const grid_views_with_column = await ncMeta.metaList2(context.workspace_id, context.base_id, _globals.MetaTable.GRID_VIEW_COLUMNS, {
            condition: {
                fk_column_id: newPvCol.id
            }
        });
        if (grid_views_with_column.length) {
            for (const gv of grid_views_with_column){
                await _View.default.fixPVColumnForView(context, gv.fk_view_id, ncMeta);
            }
        }
        // use set to avoid duplicate
        const relatedModelIds = new Set();
        // clear all single query cache of related views
        for (const col of model.columns){
            if (!(0, _nocodbsdk.isLinksOrLTAR)(col)) continue;
            const colOptions = await col.getColOptions(context);
            relatedModelIds.add(colOptions == null ? void 0 : colOptions.fk_related_model_id);
        }
        await Promise.all(Array.from(relatedModelIds).map(async (modelId)=>{
            await _View.default.clearSingleQueryCache(context, modelId, null, ncMeta);
        }));
        return true;
    }
    static async setAsMm(context, id, ncMeta = _Noco.default.ncMeta) {
        // set meta
        await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
            mm: true
        }, id);
        await _NocoCache.default.update(context, `${_globals.CacheScope.MODEL}:${id}`, {
            mm: true
        });
    }
    static async getByAliasOrId(context, { base_id, source_id, aliasOrId }, ncMeta = _Noco.default.ncMeta) {
        const cacheKey = source_id ? `${_globals.CacheScope.MODEL_ALIAS}:${base_id}:${source_id}:${aliasOrId}` : `${_globals.CacheScope.MODEL_ALIAS}:${base_id}:${aliasOrId}`;
        const modelId = base_id && aliasOrId && await _NocoCache.default.get(context, cacheKey, _globals.CacheGetType.TYPE_STRING);
        if (!modelId) {
            const model = source_id ? await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
                base_id,
                source_id
            }, null, {
                _and: [
                    modelOrViewXcCondition,
                    {
                        _or: [
                            {
                                id: {
                                    eq: aliasOrId
                                }
                            },
                            {
                                title: {
                                    eq: aliasOrId
                                }
                            }
                        ]
                    }
                ]
            }) : await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, {
                base_id
            }, null, {
                _and: [
                    modelOrViewXcCondition,
                    {
                        _or: [
                            {
                                id: {
                                    eq: aliasOrId
                                }
                            },
                            {
                                title: {
                                    eq: aliasOrId
                                }
                            }
                        ]
                    }
                ]
            });
            if (model) {
                await _NocoCache.default.set(context, cacheKey, model.id);
                await _NocoCache.default.set(context, `${_globals.CacheScope.MODEL}:${model.id}`, model);
            }
            return this.castType(model);
        }
        return modelId && this.get(context, modelId);
    }
    static async checkTitleAvailable(context, { table_name, source_id, exclude_id }, ncMeta = _Noco.default.ncMeta) {
        return !await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, _extends({
            table_name
        }, source_id ? {
            source_id
        } : {}), null, {
            _and: [
                _extends({}, modelOrViewXcCondition, exclude_id ? {
                    id: {
                        neq: exclude_id
                    }
                } : {})
            ]
        });
    }
    static async checkAliasAvailable(context, { title, source_id, exclude_id }, ncMeta = _Noco.default.ncMeta) {
        return !await ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, _extends({
            title
        }, source_id ? {
            source_id
        } : {}), null, {
            _and: [
                _extends({}, modelOrViewXcCondition, exclude_id ? {
                    id: {
                        neq: exclude_id
                    }
                } : {})
            ]
        });
    }
    async getAliasColObjMap(context, columns) {
        const mapColumns = columns || await this.getColumns(context);
        const idReduce = mapColumns.reduce((sortAgg, c)=>_extends({}, sortAgg, {
                [c.id]: c
            }), {});
        // 2nd reduce start by using idReduce props
        // if title is same as column id, even if it's a different column, it'll take priority over id
        return mapColumns.reduce((sortAgg, c)=>_extends({}, sortAgg, {
                [c.title]: c
            }), idReduce);
    }
    // For updating table meta
    static async updateMeta(context, tableId, model, ncMeta = _Noco.default.ncMeta) {
        const updateObj = (0, _extractProps.extractProps)(model, [
            'description',
            'meta'
        ]);
        // set meta
        const res = await ncMeta.metaUpdate(context.workspace_id, context.base_id, _globals.MetaTable.MODELS, (0, _modelUtils.prepareForDb)(updateObj), tableId, modelOrViewXcCondition);
        await _NocoCache.default.update(context, `${_globals.CacheScope.MODEL}:${tableId}`, (0, _modelUtils.prepareForResponse)(updateObj));
        return res;
    }
    constructor(data){
        Object.assign(this, data);
    }
};
_ts_decorate([
    (0, _nccachedecorator.NcCache)({
        key: (args)=>args[1]
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _config.NcContext === "undefined" ? Object : _config.NcContext,
        String,
        void 0
    ]),
    _ts_metadata("design:returntype", Promise)
], Model, "get", null);
_ts_decorate([
    (0, _nccachedecorator.NcCache)({
        key: (args)=>`${args[1].id || `${args[1].base_id}:${args[1].source_id}:${args[1].table_name}`}`
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _config.NcContext === "undefined" ? Object : _config.NcContext,
        Object,
        void 0
    ]),
    _ts_metadata("design:returntype", Promise)
], Model, "getByIdOrName", null);
_ts_decorate([
    (0, _nccachedecorator.NcCache)({
        key: (args)=>`${args[1].base_id}:${args[1].source_id}:${args[1].aliasOrId}`
    }),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _config.NcContext === "undefined" ? Object : _config.NcContext,
        Object,
        void 0
    ]),
    _ts_metadata("design:returntype", Promise)
], Model, "getByAliasOrId", null);
